import type { ResultOf } from '@graphql-typed-document-node/core';
import { useApolloClient } from '@apollo/client';
import { isNil } from 'es-toolkit';
import { toast } from 'sonner';
import { TaxEnrollmentsQuery } from 'src/components/advisor-dashboard/taxes/tax-content';
import { graphql } from 'src/gql';
import { HouseholdTaxesQuery } from 'src/pages/advisor/dashboard/client/[salesforceAccountId]/tax';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import type { TaxEnrollmentTypeName } from '@savvy/constants/generated/gql-enums';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import type { TaxEnrollmentFormFragment } from './tax-enrollment-form';
import { CURRENT_TAX_YEAR } from './tax-utils';

export const BulkUpdateTaxEnrollmentsToNotInterestedMutation = graphql(/* GraphQL */ `
  mutation BulkUpdateTaxEnrollmentsToNotInterested($input: BulkUpdateTaxEnrollmentsToNotInterestedInput!) {
    bulkUpdateTaxEnrollmentsToNotInterested(input: $input) {
      successIds
      failedIds
    }
  }
`);

export const CreateTaxEnrollmentMutation = graphql(/* GraphQL */ `
  mutation CreateTaxEnrollment($input: CreateTaxEnrollmentInput!) {
    createTaxEnrollment(input: $input) {
      taxEnrollment {
        id
      }
    }
  }
`);

export const UpdateTaxEnrollmentMutation = graphql(/* GraphQL */ `
  mutation UpdateTaxEnrollment($input: UpdateTaxEnrollmentInput!) {
    updateTaxEnrollment(input: $input) {
      taxEnrollment {
        id
      }
    }
  }
`);

export const DeleteTaxEnrollmentMutation = graphql(/* GraphQL */ `
  mutation DeleteTaxEnrollment($input: DeleteTaxEnrollmentInput!) {
    deleteTaxEnrollment(input: $input) {
      success
    }
  }
`);

export type CreateTaxEnrollmentInput = {
  taxYear: number;
  price: string | null;
  enrollmentType: TaxEnrollmentTypeName;
  billableClientId: string;
  nonBillableClientId?: string;
  lateFiling: boolean;
};

export type TaxEnrollmentValues = {
  enrollmentType: TaxEnrollmentTypeName;
  billingAmount: string | null;
  coClientEnrollmentType: TaxEnrollmentTypeName;
  coClientBillingAmount: string | null;
  isLateFiling: boolean;
};

export function useTaxEnrollmentActions() {
  const client = useApolloClient();
  const [createTaxEnrollment] = useMutationWithErrorLogging(CreateTaxEnrollmentMutation);
  const [updateTaxEnrollment] = useMutationWithErrorLogging(UpdateTaxEnrollmentMutation);
  const [deleteTaxEnrollment] = useMutationWithErrorLogging(DeleteTaxEnrollmentMutation);

  async function invalidateAndRefetchCaches() {
    // Refetch queries in current tab
    await Promise.all([
      client.refetchQueries({ include: [TaxEnrollmentsQuery] }),
      client.refetchQueries({ include: [HouseholdTaxesQuery] }),
    ]);

    // Notify other tabs via localStorage (storage event only fires in other tabs)
    if (typeof window !== 'undefined') {
      const currentVersion = Number(localStorage.getItem('taxEnrollmentVersion') ?? '0');
      const newVersion = currentVersion + 1;
      localStorage.setItem('taxEnrollmentVersion', String(newVersion));
    }
  }

  async function createNewEnrollments(
    data: TaxEnrollmentValues,
    household: ResultOf<typeof TaxEnrollmentFormFragment>,
  ) {
    try {
      const primarybillable = data.enrollmentType === 'savvy_joint' || data.enrollmentType === 'savvy_single';
      const coClientBillable = data.coClientEnrollmentType === 'savvy_single';

      const primaryClientInput: CreateTaxEnrollmentInput = {
        taxYear: CURRENT_TAX_YEAR,
        price: primarybillable ? data.billingAmount : null,
        enrollmentType: data.enrollmentType,
        billableClientId: household.primaryClient.id,
        nonBillableClientId: data.enrollmentType === 'savvy_joint' ? household.coClient?.id : undefined,
        lateFiling: data.isLateFiling,
      };

      // If joint filing, only create one enrollment
      if (data.enrollmentType === 'savvy_joint') {
        await createTaxEnrollment({
          variables: { input: primaryClientInput },
        });
      } else {
        // For single filing, create separate enrollments if co-client exists
        const enrollments = [
          createTaxEnrollment({
            variables: { input: primaryClientInput },
          }),
        ];

        if (household.coClient) {
          const coClientInput: CreateTaxEnrollmentInput = {
            taxYear: CURRENT_TAX_YEAR,
            price: coClientBillable ? data.coClientBillingAmount : null,
            enrollmentType: data.coClientEnrollmentType,
            billableClientId: household.coClient.id,
            lateFiling: data.isLateFiling,
          };

          enrollments.push(
            createTaxEnrollment({
              variables: { input: coClientInput },
            }),
          );
        }

        await Promise.all(enrollments);
      }

      await invalidateAndRefetchCaches();
      toast.success('Tax enrollments created successfully');
    } catch (error) {
      handleMutationError(error, 'Failed to create tax enrollments. Please try again.');
      console.error('Failed to create tax enrollments:', error);
    }
  }

  async function updateExistingEnrollments(
    data: TaxEnrollmentValues,
    household: ResultOf<typeof TaxEnrollmentFormFragment>,
  ) {
    try {
      const primarybillable = data.enrollmentType === 'savvy_joint' || data.enrollmentType === 'savvy_single';
      const coClientBillable = data.coClientEnrollmentType === 'savvy_single';

      const primaryEnrollment = household.taxEnrollments?.find((enrollment) => {
        return enrollment.billableClient.id === household.primaryClient.id;
      });
      const coClientEnrollment = household.taxEnrollments?.find((enrollment) => {
        return enrollment.billableClient.id === household.coClient?.id;
      });

      // Single -> Joint: Delete co-client enrollment first
      if (data.enrollmentType === 'savvy_joint' && coClientEnrollment !== undefined) {
        await deleteTaxEnrollment({
          variables: { input: { id: coClientEnrollment.id } },
        });
      }

      // Update existing enrollments
      const updateEnrollments = [];

      if (!isNil(primaryEnrollment)) {
        updateEnrollments.push(
          updateTaxEnrollment({
            variables: {
              input: {
                id: primaryEnrollment.id,
                price: primarybillable ? data.billingAmount : null,
                enrollmentType: data.enrollmentType,
                nonBillableClientId: data.enrollmentType === 'savvy_joint' ? household.coClient?.id : null,
              },
            },
          }),
        );
      }

      if (coClientEnrollment !== undefined && data.enrollmentType !== 'savvy_joint') {
        updateEnrollments.push(
          updateTaxEnrollment({
            variables: {
              input: {
                id: coClientEnrollment.id,
                price: coClientBillable ? data.coClientBillingAmount : null,
                enrollmentType: data.coClientEnrollmentType,
              },
            },
          }),
        );
      }

      await Promise.all(updateEnrollments);

      //Joint -> Single: Create co-client enrollment if it doesn't exist
      if (data.enrollmentType !== 'savvy_joint' && household.coClient !== null && coClientEnrollment === undefined) {
        await createTaxEnrollment({
          variables: {
            input: {
              taxYear: CURRENT_TAX_YEAR,
              price: coClientBillable ? data.coClientBillingAmount : null,
              enrollmentType: data.coClientEnrollmentType,
              billableClientId: household.coClient.id,
            },
          },
        });
      }

      await invalidateAndRefetchCaches();
      toast.success('Tax enrollments updated successfully');
    } catch (error) {
      handleMutationError(error, 'Failed to update tax enrollments. Please try again.');
      console.error('Failed to update tax enrollments:', error);
    }
  }

  return {
    createNewEnrollments,
    updateExistingEnrollments,
  };
}
